import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { UserService, User } from '../../services/UserService';

interface UserListProps { onUserSelect: (user: User) => void; }
export interface UserListRef { fetchUsers: () => void; }

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
  background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none',
  borderRadius: '12px', padding: '3px 10px', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 8px 14px rgba(14,52,161,0.25)', transition: 'transform 0.15s ease, box-shadow 0.15s ease', fontSize: '0.75rem', minWidth: '72px',
};
const dangerBtn: React.CSSProperties = { ...primaryBtn, background: 'linear-gradient(120deg, #b3182a, #e7384f)', boxShadow: '0 10px 22px rgba(194,43,60,0.3)' };

const UserList = forwardRef<UserListRef, UserListProps>(({ onUserSelect: _onUserSelect }, ref) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await UserService.getAllUsers();
      setUsers(data || []);
      setError(null); setActionError(null);
    } catch (err) {
      setError('Erro ao carregar usuários');
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);
  useImperativeHandle(ref, () => ({ fetchUsers }));

  const handleToggleUser = async (user: User) => {
    try {
      setUpdatingUserId(user.id); setActionError(null);
      await UserService.setUserActive(user.id, !user.is_active);
      await fetchUsers();
    } catch {
      setActionError('Não foi possível atualizar o status do usuário.');
    } finally { setUpdatingUserId(null); }
  };

  if (loading) return <div className="p-10 text-center text-[#666] bg-white rounded-lg shadow-sm mt-5">Carregando lista de usuários...</div>;
  if (error) return <div className="p-10 text-center text-red-600 bg-red-50 border border-red-200 rounded-lg mt-5">{error}</div>;

  const thClass = "px-2.5 py-2 text-left font-semibold text-[#333] text-[0.85rem] bg-[#f8f9fa] border-b-2 border-[#dee2e6]";
  const tdClass = "px-2.5 py-2 border-b border-[#dee2e6] text-[#555] text-[0.85rem]";

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-x-auto mt-5">
      {actionError && <div className="mx-4 mb-3 text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded text-sm">{actionError}</div>}
      <table className="w-full min-w-[1200px] border-collapse">
        <thead>
          <tr>
            {['ID', 'Nome', 'Nome de Usuário', 'Email', 'Celular', 'Cargo', 'Status', 'Data de Criação', 'Última Atualização', 'Ações'].map(h => (
              <th key={h} className={thClass}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.length > 0 ? users.map(user => (
            <tr key={user.id} className="transition-colors hover:bg-[#f1f1f1]">
              <td className={tdClass}>{user.id}</td>
              <td className={tdClass}>{user.full_name || '-'}</td>
              <td className={tdClass}>{user.username}</td>
              <td className={tdClass}>{user.email}</td>
              <td className={tdClass}>{user.phone || '-'}</td>
              <td className={tdClass}>{user.role}</td>
              <td className={tdClass}>
                <span className={`inline-block px-2 py-1 rounded-xl font-semibold text-xs ${user.is_active ? 'bg-[#d1e7dd] text-[#0f5132]' : 'bg-[#f8d7da] text-[#842029]'}`}>
                  {user.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td className={tdClass + " min-w-[110px]"}>{new Date(user.created_at).toLocaleDateString('pt-BR')}</td>
              <td className={tdClass + " min-w-[110px]"}>{new Date(user.updated_at).toLocaleDateString('pt-BR')}</td>
              <td className={tdClass + " w-[100px] text-center"}>
                <button
                  style={user.is_active ? dangerBtn : primaryBtn}
                  onClick={() => handleToggleUser(user)}
                  disabled={updatingUserId === user.id}
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingUserId === user.id ? 'Salvando...' : user.is_active ? 'Inativar' : 'Reativar'}
                </button>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan={10} className="text-center p-10 text-[#666] italic">Nenhum usuário encontrado.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

UserList.displayName = 'UserList';
export default UserList;
